import { useMemo } from "react";
import { useAIFilteredStockData } from "hooks/useStockData.ts";
import { isAIConfigured } from "utils/aiClient.ts";
import { normalizeSuggestion, asArray } from "utils/aiNormalization.ts";
import type { SimplifiedRecipeSuggestion } from "../types/ai.ts";
import { getAISuggestions, setAISuggestions } from "utils/storage.ts";
import { useAIJSONStream } from "./useAIJSONStream.ts";
import { buildPantryArray } from "utils/aiPantry.ts";

// Product names for which we want to ignore daysUntilExpiry and push to bottom
// Match is case-insensitive and exact on the product name
const EXCLUDE_EXPIRY_PRODUCT_NAMES = new Set<string>([
  "milk"  // Add more names as needed
]);

interface SuggestionsResponse { recipes: SimplifiedRecipeSuggestion[] }

export function useAISuggestions() {
  const { stockItemsMap } = useAIFilteredStockData();

  const prioritized = useMemo(() => {
    const items = Array.from(stockItemsMap.values());
    const today = new Date();
    const parseISO = (s?: string) => {
      if (!s) return null;
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };
    const daysBetween = (a: Date, b: Date) => {
      const a0 = new Date(a); a0.setHours(0,0,0,0);
      const b0 = new Date(b); b0.setHours(0,0,0,0);
      return Math.floor((a0.getTime() - b0.getTime()) / 86400000);
    };
    const shaped = items.map(x => {
      const name = x.product.name;
      const isExcluded = EXCLUDE_EXPIRY_PRODUCT_NAMES.has(name.toLowerCase());
      const expiry = parseISO(x.stock.best_before_date);
      const computedDaysUntil = expiry ? daysBetween(expiry, today) : null;
      const clampedDaysUntil = computedDaysUntil === null ? null : Math.max(0, computedDaysUntil);
      const boundedDaysUntil = clampedDaysUntil === null ? null : (clampedDaysUntil > 1000 ? null : clampedDaysUntil);
      const daysUntil = isExcluded ? null : boundedDaysUntil;
      // For sorting treat null as very large; excluded names are forced to bottom
      const sortDays = daysUntil === null ? Number.POSITIVE_INFINITY : daysUntil;
      return {
        name,
        daysUntilExpiry: daysUntil,
        sortDays,
        amount: x.stock.amount,
        quantityUnit: x.quantityUnit.name,
        location: x.location.name,
      };
    });

    shaped.sort((a, b) => a.sortDays - b.sortDays || a.name.localeCompare(b.name));

    // Keep the previous outward shape to avoid breaking callers
    return shaped.map(({ name, daysUntilExpiry }) => ({ name, daysUntilExpiry }));
  }, [stockItemsMap]);

  const canRequest = isAIConfigured();

  const initialStored = getAISuggestions();
  const normalizeOrNull = (parsed: unknown): SimplifiedRecipeSuggestion[] | null => {
    const maybe = parsed as Partial<SuggestionsResponse>;
    const normalizeSuggestions = asArray(normalizeSuggestion, []);
    const normalized = normalizeSuggestions((maybe as SuggestionsResponse | undefined)?.recipes as unknown);
    return normalized.length > 0 ? normalized : null;
  };

  // Build the exact prompt text used for requests, based on filtered stock
  const buildPromptText = () => {
    const pantry = buildPantryArray(stockItemsMap);
    const pantryJson = JSON.stringify(pantry, null, 2);
    return [
      "You will receive a JSON array named 'pantry' where each item has: { name: string, daysUntilExpiry: number | null, amount: string, location: string }.",
      "Ingredient policy: Use only items from the provided Pantry list. You may use common staples ONLY if they appear in the Pantry list. You may always use water as needed.",
      "Prefer earlier items (sorted by daysUntilExpiry ascending), but all ingredients used (besides water) must come from the provided list.",
      "Propose five different healthy dinner recipes suitable for an average adult.",
      "Balance: Aim for roughly half of the recipes to include meat (if meat exists in the pantry), and the rest to be vegetarian. If no meat is available, provide vegetarian options only.",
      "Each recipe must serve between 2 and 4 people and take around 20-60 minutes.",
      "Return STRICT JSON with this schema: { recipes: Array<{ title: string, description: string, estimated_minutes: number, servings: number, ingredients_description: string }> }",
      "",
      "pantry:",
      pantryJson,
    ].join('\n');
  };

  const {
    value: result,
    loading,
    error,
    requestStreaming,
    cancel,
    streaming,
    rawText,
    reasoningText,
  } = useAIJSONStream<SimplifiedRecipeSuggestion[]>({
    buildPrompt: buildPromptText,
    normalizeOrNull,
    initialValue: initialStored?.recipes ?? null,
    onFinal: (val, reasoning) => {
      if (val && val.length > 0) {
        setAISuggestions({ recipes: val, savedAt: Date.now(), reasoning });
      }
    },
  });

  return {
    loading,
    error,
    result,
    requestStreaming,
    cancel,
    streaming,
    canRequest,
    prioritized,
    rawText,
    reasoningText,
    promptText: buildPromptText(),
  };
}
